import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Auth, RecaptchaVerifier } from '@angular/fire/auth';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AuthRoutes } from '@auth/auth.routes';
import { GoogleSigninComponent } from '@auth/components/google-signin/google-signin.component';
import { AuthMethods } from '@auth/models/auth-methods';
import { IUserEmailLogin, IUserEmailLoginForm, IUserPhoneLoginForm } from '@auth/models/login';
import { EmailAuthService } from '@auth/services/email-auth/email-auth.service';
import { PhoneAuthService } from '@auth/services/phone-auth/phone-auth.service';
import { PhoneNumberValidator } from '@shared/classes/phone-number-validator';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { PasswordControlComponent } from '@shared/components/forms/password-control/password-control.component';
import { PhoneNumberControlComponent } from '@shared/components/forms/phone-number-control/phone-number-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { StepNavigatorComponent } from '@shared/components/ui/step-navigator/step-navigator.component';
import { IMobileNumber } from '@shared/models/numbers';
import { IStepNavigation, IStepNavigatorConfig, NavButtonStyle, StepDisplay, StepModes, StepUI } from '@shared/models/step-navigator';

@Component({
	selector: 'app-login',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		GoogleSigninComponent,
		RouterLink,
		StandardControlComponent,
		PasswordControlComponent,
		StepNavigatorComponent,
		PhoneNumberControlComponent,
		AlertComponent,
	],
	templateUrl: './login.component.html',
	styleUrl: './login.component.scss',
	host: {
		class: 'h-100',
	},
})
export class LoginComponent implements OnInit {
	private auth = inject(Auth);
	private phoneAuthService = inject(PhoneAuthService);
	private emailAuthService = inject(EmailAuthService);
	AuthRoutes = AuthRoutes;

	navigationConfig: IStepNavigatorConfig;
	selectedStep: IStepNavigation;
	AuthMethods = AuthMethods;

	emailLoginIsLoading: boolean = false;
	verificationCodeLoading: boolean = false;
	phoneLoginIsLoading: boolean = false;

	verificationId: string | null = null;

	emailLoginForm: FormGroup<IUserEmailLoginForm> = new FormGroup<IUserEmailLoginForm>({
		email: new FormControl(null, [Validators.required, Validators.email]),
		password: new FormControl(null, [Validators.required, Validators.minLength(8)]),
	});

	phoneLoginForm: FormGroup<IUserPhoneLoginForm> = new FormGroup<IUserPhoneLoginForm>({
		phoneNumber: new FormControl<IMobileNumber | null>(null, [Validators.required, PhoneNumberValidator.matchDialCode()]),
		verificationCode: new FormControl<string | null>({ value: null, disabled: true }, [
			Validators.required,
			Validators.minLength(6),
			Validators.maxLength(6),
		]),
	});

	ngOnInit(): void {
		this.navigationConfig = {
			ui: StepUI.WIZARD,
			mode: StepModes.NG_SWITCH,
			displayType: StepDisplay.TABS,
			navButtonStyle: NavButtonStyle.CHEVRON,
			showStepChevrons: false,
			showStepNumbers: false,
			showNavButtons: false,
			steps: [
				{
					title: {
						full: 'Email',
						tabs: 'Email',
					},
					icon: 'bi-envelope-at',
					switchCase: AuthMethods.EMAIL,
				},
				{
					title: {
						full: 'Phone Number',
						tabs: 'Phone Number',
					},
					icon: 'bi-telephone',
					switchCase: AuthMethods.PHONE_NUMBER,
				},
			],
		};

		this.selectedStep = this.navigationConfig.steps[0];
	}

	handleEmailLogin(): void {
		if (this.emailLoginForm.invalid) {
			return;
		}
		this.emailLoginIsLoading = true;
		this.emailAuthService.signInWithEmailAndPassword(this.emailLoginForm.value as IUserEmailLogin).finally(() => {
			this.emailLoginIsLoading = false;
		});
	}

	get phoneNumber(): FormControl<IMobileNumber | null> {
		return this.phoneLoginForm.controls.phoneNumber;
	}

	get verificationCode(): FormControl<string | null> {
		return this.phoneLoginForm.controls.verificationCode;
	}

	get recaptchaVerifier(): RecaptchaVerifier {
		return new RecaptchaVerifier(this.auth, 'verificationCodeButton', {
			size: 'invisible',
		});
	}

	getVerificationCode(): void {
		if (this.phoneNumber.invalid) {
			return;
		}
		this.verificationCodeLoading = true;
		this.phoneNumber.disable();
		this.phoneAuthService
			.requestVerificationCode(this.phoneNumber.value!, this.recaptchaVerifier)
			.then((verificationId) => {
				this.verificationId = verificationId;
				this.verificationCode.enable();
			})
			.catch((error) => {
				console.error('Error requesting verification code:', error);
				this.phoneNumber.enable();
			})
			.finally(() => {
				this.verificationCodeLoading = false;
			});
	}

	handlePhoneNumberLogin(): void {
		if (this.verificationCode.invalid || !this.verificationId) {
			return;
		}
		this.phoneLoginIsLoading = true;
		this.phoneAuthService.signInWithPhoneNumber(this.verificationId, this.verificationCode.value!).finally(() => {
			this.phoneLoginIsLoading = false;
		});
	}

	handleResetPhoneForm(): void {
		this.phoneLoginForm.reset();
		this.verificationId = null;
		this.phoneNumber.enable();
		this.verificationCode.disable();
		this.phoneLoginIsLoading = false;
		this.verificationCodeLoading = false;
	}
}
